import React, { useState } from 'react';
import { 
  ResponsiveContainer, 
  PieChart,
  Pie,
  Tooltip, 
  Cell
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Percent, 
  Building2, 
  Shield, 
  Laptop, 
  Wrench,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Info
} from 'lucide-react';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

const GeneralAdminAnalysis = ({ language, year, onBack, flowMap }) => {
  const yearNum = parseInt(year) || 2025;
  const gaData = mockData?.generalAdmin?.[yearNum] || {};
  const prevGaData = mockData?.generalAdmin?.[yearNum - 1] || {};
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(yearNum, language);
  const previousPeriod = getPreviousPeriodLabel(yearNum, language);
  const budgetLabelFull = getBudgetLabel(yearNum, language);
  const titleSuffix = getTitleSuffix(yearNum, language);
  const comparisonSubtitle = getComparisonSubtitle(yearNum, language);
  
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  // Budget data for G&A - using actual budget data from mockData
  const budgetGaTotal = gaData.budgetTotal || gaData.total * 0.96;
  const gaVsBudget = gaData.vsBudget || ((gaData.total - budgetGaTotal) / budgetGaTotal * 100).toFixed(1);
  const gaYoY = prevGaData.total ? ((gaData.total - prevGaData.total) / prevGaData.total * 100).toFixed(1) : '0';

  // Calculate G&A per carat (regular carats recovered)
  const costPerCaratData = mockData.costPerCarat?.[yearNum] || {};
  const prevCostPerCaratData = mockData.costPerCarat?.[yearNum - 1] || {};
  const regularCarats = costPerCaratData.kpis?.regularCarats?.value || 920;
  const prevRegularCarats = prevCostPerCaratData.kpis?.regularCarats?.value || 820;
  
  const gaPerCarat = gaData.total && regularCarats > 0 ? Math.round((gaData.total * 1000) / regularCarats) : 0;
  const prevGaPerCarat = prevGaData.total && prevRegularCarats > 0 ? Math.round((prevGaData.total * 1000) / prevRegularCarats) : 0;
  const gaPerCaratChange = prevGaPerCarat > 0 ? ((gaPerCarat - prevGaPerCarat) / prevGaPerCarat * 100).toFixed(1) : '0';

  // Category definitions
  const categories = [
    {
      id: 'supportServices',
      key: 'supportServices',
      name: { pt: 'Serviços de Suporte', en: 'Support Services' },
      icon: Building2,
      color: '#0A192F',
      lightColor: '#0A192F20',
      description: {
        pt: 'Capacidade técnica, analítica e profissional adquirida externamente',
        en: 'Technical, analytical and professional capacity acquired externally'
      }
    },
    {
      id: 'structureCosts',
      key: 'structureCosts',
      name: { pt: 'Custos de Estrutura', en: 'Structure Costs' },
      icon: Wrench,
      color: '#C5A059',
      lightColor: '#C5A05920',
      description: {
        pt: 'Custo-base de funcionamento da organização',
        en: 'Base operating cost of the organization'
      }
    },
    {
      id: 'securityInsurance',
      key: 'securityInsurance',
      name: { pt: 'Segurança, Seguros e Responsabilidade', en: 'Security, Insurance & Responsibility' },
      icon: Shield,
      color: '#10B981',
      lightColor: '#10B98120',
      description: {
        pt: 'Custo de protecção, resiliência e licença para operar',
        en: 'Cost of protection, resilience and license to operate'
      }
    },
    {
      id: 'itCommunication',
      key: 'itCommunication',
      name: { pt: 'IT, Comunicação e Publicidade', en: 'IT, Communication & Advertising' },
      icon: Laptop,
      color: '#3B82F6',
      lightColor: '#3B82F620',
      description: {
        pt: 'Infraestrutura digital crítica e comunicação corporativa',
        en: 'Critical digital infrastructure and corporate communication'
      }
    }
  ];

  // Prepare pie chart data for category distribution with explosion effect
  const categoryPieData = categories.map((cat, idx) => {
    const isSelected = expandedCategory === cat.id;
    const hasSelection = expandedCategory !== null;
    return {
      name: cat.name[language],
      value: gaData[cat.key]?.total || 0,
      fill: cat.color,
      percentage: gaData.total ? ((gaData[cat.key]?.total / gaData.total) * 100).toFixed(1) : '0',
      // Explosion effect: selected slice moves out, others shrink
      innerRadius: hasSelection ? (isSelected ? 55 : 65) : 60,
      outerRadius: hasSelection ? (isSelected ? 115 : 85) : 100,
    };
  });

  // Get Top 5 items across all categories
  const getAllItems = () => {
    const allItems = [];
    categories.forEach(cat => {
      const catData = gaData[cat.key];
      if (catData?.items) {
        catData.items.forEach(item => {
          allItems.push({
            name: item.name[language],
            value: item.value,
            category: cat.name[language],
            color: cat.color
          });
        });
      }
    });
    return allItems.sort((a, b) => b.value - a.value).slice(0, 5);
  };
  const top5Items = getAllItems();
  const top5Total = top5Items.reduce((sum, item) => sum + item.value, 0);
  const top5Percentage = gaData.total ? ((top5Total / gaData.total) * 100).toFixed(0) : '0';

  // Calculate G&A / OPEX percentage (assuming OPEX total from mockData)
  const opexTotal = mockData?.opexData?.[yearNum]?.total || 120; // fallback value
  const prevOpexTotal = mockData?.opexData?.[yearNum - 1]?.total || 110;
  const gaOpexPercentage = opexTotal ? ((gaData.total / opexTotal) * 100).toFixed(1) : '0';
  const prevGaOpexPercentage = prevOpexTotal && prevGaData.total 
    ? ((prevGaData.total / prevOpexTotal) * 100).toFixed(1) : '0';
  const gaOpexPpChange = (parseFloat(gaOpexPercentage) - parseFloat(prevGaOpexPercentage)).toFixed(1);

  // Main KPIs
  const mainKpis = [
    {
      title: { pt: 'Gerais e Administrativos', en: 'General & Admin' },
      value: `${(gaData.total || 0).toFixed(1)}M`,
      unit: 'USD',
      icon: DollarSign,
      color: 'bg-primary',
      vsBudget: gaVsBudget,
      yoyChange: gaYoY
    },
    {
      title: { pt: 'Peso nos Custos Operacionais', en: 'Weight in Operating Costs' },
      value: `${gaOpexPercentage}%`,
      ppChange: gaOpexPpChange,
      vsBudgetPp: '+0.8',
      icon: Target,
      color: 'bg-slate-600'
    },
    {
      title: { pt: 'Top 5 Itens / Gerais e Administrativos', en: 'Top 5 Items / General & Admin' },
      value: `${top5Percentage}%`,
      subtitle: `${top5Total.toFixed(1)}M USD`,
      vsBudget: '+2.1',
      icon: Percent,
      color: 'bg-blue-600'
    },
    {
      title: { pt: 'G&A/Quilate Recuperado (Pedras Regulares)', en: 'G&A/Recovered Carat (Regular Stones)' },
      value: `${gaPerCarat}`,
      unit: 'USD',
      change: gaPerCaratChange,
      vsBudget: '+6.2',
      isPositive: parseFloat(gaPerCaratChange) < 0,
      icon: DollarSign,
      color: 'bg-emerald-600'
    },
  ];

  // Key insights - same structure as CMVMC
  const insights = [
    {
      driver: { pt: 'Capacitação Externa', en: 'External Capacity' },
      detail: { 
        pt: 'G&A: custos operacionais de terceiros, essenciais para a operação.',
        en: 'G&A: third-party operating costs essential for operations.'
      },
      direction: gaData.yoyChange > 0 ? 'up' : 'down',
      impact: 'high'
    },
    {
      driver: { pt: 'Custos Fixos e Variáveis', en: 'Fixed & Variable Costs' },
      detail: { 
        pt: 'Mix de custos recorrentes (rendas) e variáveis (actividade).',
        en: 'Mix of recurring costs (rentals) and variable costs (activity).'
      },
      direction: 'neutral',
      impact: 'medium'
    },
    {
      driver: { pt: 'Flexibilidade Operacional', en: 'Operational Flexibility' },
      detail: { 
        pt: 'Ajuste de capacidade sem alterar estrutura permanente.',
        en: 'Capacity adjustment without changing permanent structure.'
      },
      direction: 'down',
      impact: 'low'
    },
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-800 text-white px-4 py-3 rounded-lg shadow-lg">
          <p className="font-semibold mb-1">{data.name}</p>
          <p className="text-lg font-bold">{(data.value || 0).toFixed(2)}M USD</p>
          <p className="text-xs text-slate-300">{data.percentage}% {language === 'pt' ? 'do total' : 'of total'}</p>
        </div>
      );
    }
    return null;
  };

  // Render category detail section
  const renderCategoryDetail = (category) => {
    const catData = gaData[category.key];
    if (!catData) return null;

    const items = catData.items || [];
    const catBudgetTotal = catData.budgetTotal || catData.total * 0.96;
    const catVsBudget = catData.vsBudget || ((catData.total - catBudgetTotal) / catBudgetTotal * 100);
    const prevYearTotal = catData.prevYear || catData.total * 0.78;
    const yoyChange = catData.yoyChange || ((catData.total - prevYearTotal) / prevYearTotal * 100);
    
    // Find month with highest cost (simulated) - FULL MONTH NAMES
    const monthsFull = language === 'pt' 
      ? ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const peakMonthIdx = 8; // September (fixed for consistency)
    const peakMonth = monthsFull[peakMonthIdx];
    const peakMonthValue = (catData.total / 12 * 1.35).toFixed(2);
    const peakMonthVsBudget = ((catData.total / 12 * 1.35 - catBudgetTotal / 12) / (catBudgetTotal / 12) * 100).toFixed(1);
    const peakMonthYoY = ((catData.total / 12 * 1.35 - prevYearTotal / 12) / (prevYearTotal / 12) * 100).toFixed(1);
    
    // Find item with highest cost
    const sortedItems = [...items].sort((a, b) => b.value - a.value);
    const topItem = sortedItems[0];
    const topItemBudget = topItem?.budget || topItem?.value * 0.96;
    const topItemVsBudget = topItem ? ((topItem.value - topItemBudget) / topItemBudget * 100).toFixed(1) : 0;
    const topItemYoY = topItem?.prevYear ? ((topItem.value - topItem.prevYear) / topItem.prevYear * 100).toFixed(1) : 0;

    // Trend indicators
    const totalTrendPositive = yoyChange <= 0;
    const peakMonthTrendPositive = parseFloat(peakMonthYoY) <= 0;
    const topItemTrendPositive = parseFloat(topItemYoY) <= 0;

    return (
      <div className="space-y-6 mt-6 border-t border-slate-200 pt-6">
        {/* Category KPIs - Sticky header for scroll */}
        <div className="bg-white sticky top-0 z-10 pb-4">
          <div className="grid grid-cols-3 gap-4">
            {/* KPI 1 - Category Total (with name) */}
            <div className="bg-slate-50 rounded-xl p-4 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${totalTrendPositive ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {yoyChange > 0 ? (
                  <ArrowUpRight className="w-4 h-4" style={{ color: '#EF4444' }} />
                ) : yoyChange < 0 ? (
                  <ArrowDownRight className="w-4 h-4" style={{ color: '#10B981' }} />
                ) : (
                  <Minus className="w-4 h-4" style={{ color: '#94A3B8' }} />
                )}
              </div>
              <p className="text-xs text-slate-500 mb-1">{category.name[language]}</p>
              <p className="font-serif text-2xl font-bold text-primary">{(catData.total || 0).toFixed(1)}<span className="text-sm text-slate-400 ml-1">M USD</span></p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${catVsBudget <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {catVsBudget > 0 ? '+' : ''}{catVsBudget.toFixed(1)}% Orç.
                </span>
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${yoyChange <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {yoyChange > 0 ? '+' : ''}{yoyChange.toFixed(1)}% YoY
                </span>
              </div>
            </div>
            
            {/* KPI 2 - Month with highest cost - FULL NAME */}
            <div className="bg-slate-50 rounded-xl p-4 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${peakMonthTrendPositive ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {parseFloat(peakMonthYoY) > 0 ? (
                  <ArrowUpRight className="w-4 h-4" style={{ color: '#EF4444' }} />
                ) : parseFloat(peakMonthYoY) < 0 ? (
                  <ArrowDownRight className="w-4 h-4" style={{ color: '#10B981' }} />
                ) : (
                  <Minus className="w-4 h-4" style={{ color: '#94A3B8' }} />
                )}
              </div>
              <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Mês com Maior Custo' : 'Highest Cost Month'}</p>
              <p className="font-serif text-xl font-bold text-primary">{peakMonth}</p>
              <p className="text-sm text-slate-600 font-semibold">{peakMonthValue}M USD</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(peakMonthVsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {parseFloat(peakMonthVsBudget) > 0 ? '+' : ''}{peakMonthVsBudget}% Orç.
                </span>
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(peakMonthYoY) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {parseFloat(peakMonthYoY) > 0 ? '+' : ''}{peakMonthYoY}% YoY
                </span>
              </div>
            </div>
            
            {/* KPI 3 - Item with highest cost - NO TRUNCATION */}
            <div className="bg-slate-50 rounded-xl p-4 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${topItemTrendPositive ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {parseFloat(topItemYoY) > 0 ? (
                  <ArrowUpRight className="w-4 h-4" style={{ color: '#EF4444' }} />
                ) : parseFloat(topItemYoY) < 0 ? (
                  <ArrowDownRight className="w-4 h-4" style={{ color: '#10B981' }} />
                ) : (
                  <Minus className="w-4 h-4" style={{ color: '#94A3B8' }} />
                )}
              </div>
              <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Rubrica com Maior Custo' : 'Highest Cost Item'}</p>
              <p className="font-serif text-sm font-bold text-primary leading-tight">{topItem?.name[language]}</p>
              <p className="text-sm text-slate-600 font-semibold mt-1">{topItem?.value.toFixed(1)}M USD</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(topItemVsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {parseFloat(topItemVsBudget) > 0 ? '+' : ''}{topItemVsBudget}% Orç.
                </span>
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(topItemYoY) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {parseFloat(topItemYoY) > 0 ? '+' : ''}{topItemYoY}% YoY
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Items breakdown - 7 Column Table with scroll */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="font-serif text-lg font-semibold text-primary mb-4 sticky top-0 bg-white">
            {language === 'pt' ? 'Decomposição por Sub-rubrica' : 'Breakdown by Sub-item'}
          </h4>
          {/* Scrollable table container */}
          <div className="max-h-[400px] overflow-y-auto">
            {/* Column Headers - 7 columns */}
            <div className="flex items-center justify-between py-2 border-b-2 border-slate-200 mb-2 sticky top-0 bg-white">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-3 h-3"></div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  {language === 'pt' ? 'Rubrica' : 'Item'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-14 text-right">{yearNum}</span>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-14 text-right">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</span>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-14 text-right">{language === 'pt' ? 'Var. Abs.' : 'Var. Abs.'}</span>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-14 text-right">{language === 'pt' ? 'Var. %' : 'Var. %'}</span>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-14 text-right">{yearNum - 1}</span>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide w-14 text-right">{language === 'pt' ? 'Var. YoY' : 'Var. YoY'}</span>
              </div>
            </div>
            <div className="space-y-1">
              {items.map((item, idx) => {
                const yoyVar = item.prevYear ? ((item.value - item.prevYear) / item.prevYear * 100).toFixed(1) : 0;
                const budget = item.budget || item.value * 0.96;
                const vsBudgetVar = item.vsBudget || ((item.value - budget) / budget * 100).toFixed(1);
                const absVar = (item.value - budget).toFixed(2);
                
                return (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3 flex-1">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: category.color, opacity: 1 - (idx * 0.05) }}
                      ></div>
                      <span className="text-sm text-slate-700">{item.name[language]}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-primary w-14 text-right">{item.value.toFixed(1)}M</span>
                      <span className="text-sm text-slate-500 w-14 text-right">{budget.toFixed(2)}M</span>
                      <span className={`text-xs w-14 text-right ${parseFloat(absVar) <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {parseFloat(absVar) > 0 ? '+' : ''}{absVar}M
                      </span>
                      <span className={`text-xs w-14 text-right font-medium ${parseFloat(vsBudgetVar) <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {parseFloat(vsBudgetVar) > 0 ? '+' : ''}{vsBudgetVar}%
                      </span>
                      <span className="text-xs text-slate-400 w-14 text-right">{(item.prevYear || 0).toFixed(1)}M</span>
                      <span className={`text-xs w-14 text-right font-medium ${parseFloat(yoyVar) <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {yoyVar > 0 ? '+' : ''}{yoyVar}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Table Total Row - Outside scroll */}
          <div className="flex items-center justify-between py-3 border-t-2 border-slate-300 mt-2 bg-slate-50 -mx-6 px-6">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-3 h-3"></div>
              <span className="text-sm font-bold text-primary">{language === 'pt' ? 'Total' : 'Total'}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-bold text-primary w-14 text-right">{(catData.total || 0).toFixed(1)}M</span>
              <span className="text-sm font-semibold text-slate-500 w-14 text-right">{(catBudgetTotal || 0).toFixed(1)}M</span>
              <span className={`text-xs font-semibold w-14 text-right ${(catData.total - catBudgetTotal) <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {(catData.total - catBudgetTotal) > 0 ? '+' : ''}{(catData.total - catBudgetTotal).toFixed(1)}M
              </span>
              <span className={`text-xs font-semibold w-14 text-right ${catVsBudget <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {catVsBudget > 0 ? '+' : ''}{catVsBudget.toFixed(1)}%
              </span>
              <span className="text-xs text-slate-400 font-semibold w-14 text-right">{(catData.prevYear || 0).toFixed(1)}M</span>
              <span className={`text-xs font-semibold w-14 text-right ${catData.yoyChange <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {catData.yoyChange > 0 ? '+' : ''}{catData.yoyChange?.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="general-admin-analysis">
      {/* Header */}
      <div>
        {onBack && (
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
            style={{ color: '#7A859D' }}
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'pt' ? 'Voltar a Custos Operacionais' : 'Back to Operating Costs'}
          </button>
        )}
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Gerais e Administrativos' : 'General & Administrative'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights - Mensagens-Chave (same style as CMVMC) */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {insights.map((insight, idx) => {
            const driverName = typeof insight.driver === 'object' ? insight.driver[language] : insight.driver;
            const detail = typeof insight.detail === 'object' ? insight.detail[language] : insight.detail;
            const isUp = insight.direction === 'up';
            const isNeutral = insight.direction === 'neutral';
            const impactBg = insight.impact === 'high' ? 'bg-red-500/20' : insight.impact === 'medium' ? 'bg-amber-500/20' : 'bg-blue-500/20';
            const impactBorder = insight.impact === 'high' ? 'border-red-500/30' : insight.impact === 'medium' ? 'border-amber-500/30' : 'border-blue-500/30';
            
            return (
              <div 
                key={idx} 
                className={`${impactBg} ${impactBorder} border rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isNeutral ? 'bg-amber-500/30' : isUp ? 'bg-red-500/30' : 'bg-emerald-500/30'
                  }`}>
                    {isNeutral ? (
                      <Percent className="w-5 h-5 text-amber-400" />
                    ) : isUp ? (
                      <TrendingUp className="w-5 h-5 text-red-400" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-white text-sm mb-1.5 leading-tight">{driverName}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main KPIs - same style as CMVMC */}
      <div className="grid grid-cols-4 gap-4">
        {mainKpis.map((kpi, idx) => {
          // Calculate trend for arrow indicator
          const numChange = parseFloat(kpi.yoyChange || kpi.change || kpi.ppChange) || 0;
          const isPositiveTrend = kpi.isPositive !== undefined ? kpi.isPositive : numChange <= 0;
          const trendBg = isPositiveTrend ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositiveTrend ? '#10B981' : '#EF4444';
          const TrendIconArrow = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          const hasChange = kpi.yoyChange !== undefined || kpi.change !== undefined || kpi.ppChange !== undefined;
          
          return (
          <div key={idx} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative">
            {/* Trend Arrow Indicator */}
            {hasChange && (
              <div 
                className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}
              >
                <TrendIconArrow className="w-4 h-4" style={{ color: trendColor }} />
              </div>
            )}
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100">
                <kpi.icon className="w-5 h-5 text-primary" />
              </div>
            </div>
            <p className="text-xs text-slate-500 tracking-wider mb-1">{kpi.title[language]}</p>
            <p className="font-serif text-2xl font-semibold text-primary">
              {kpi.value}
              {kpi.unit && <span className="text-sm text-slate-400 ml-1 font-sans">{kpi.unit}</span>}
            </p>
            {kpi.subtitle && <p className="text-xs text-slate-400 mt-1">{kpi.subtitle}</p>}
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {kpi.vsBudget && (
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                  parseFloat(kpi.vsBudget) <= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                }`}>
                  {parseFloat(kpi.vsBudget) > 0 ? '+' : ''}{kpi.vsBudget}% Orç.
                </div>
              )}
              {kpi.vsBudgetPp && (
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                  parseFloat(kpi.vsBudgetPp) <= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                }`}>
                  {parseFloat(kpi.vsBudgetPp) > 0 ? '+' : ''}{kpi.vsBudgetPp}p.p. vs Orç.
                </div>
              )}
              {kpi.yoyChange && (
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                  parseFloat(kpi.yoyChange) <= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                }`}>
                  {parseFloat(kpi.yoyChange) > 0 ? '+' : ''}{kpi.yoyChange}% YoY
                </div>
              )}
              {kpi.ppChange !== undefined && (
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                  parseFloat(kpi.ppChange) <= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                }`}>
                  {parseFloat(kpi.ppChange) > 0 ? '+' : ''}{kpi.ppChange} pp YoY
                </div>
              )}
              {kpi.change !== undefined && (
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                  kpi.isPositive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                }`}>
                  {kpi.isPositive ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                  {parseFloat(kpi.change) > 0 ? '+' : ''}{kpi.change}% YoY
                </div>
              )}
            </div>
          </div>
        )})}
      </div>

      {/* Sumário Executivo - Executive Summary - Following CMVMC style */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Custos gerais e administrativos' : 'General and administrative costs'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="general-admin-executive-summary"
      >
        {/* Visão Global */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-3">
            {language === 'pt' 
              ? 'Em 2025, Gerais e Administrativos totalizou USD 35,6M, ficando 4,1% acima do orçamento e 27,1% acima de 2024. A pressão esteve distribuída pelos quatro blocos da rubrica, com maior peso em Custos de Estrutura (USD 13,2M; 37%) e Serviços de Suporte (USD 12,5M; 35%), que em conjunto representam 72% do total. A concentração por item individual é moderada: os cinco maiores custos do ano — Combustíveis e Lubrificantes (USD 2,3M), Vigilância e Segurança (USD 2,3M), Serviços Técnicos de Engenharia (USD 2,0M), Rendas de Imóveis (USD 1,9M) e Assistência Técnica (USD 1,8M) — somaram USD 10,3M, ou 29% da rubrica.'
              : 'In 2025, General & Admin totaled USD 35.6M, 4.1% above budget and 27.1% above 2024. Pressure was distributed across the four blocks, with greater weight in Structure Costs (USD 13.2M; 37%) and Support Services (USD 12.5M; 35%), which together represent 72% of total.'}
          </p>
          <p className="text-slate-700 leading-relaxed text-sm">
            {language === 'pt' 
              ? 'No plano mensal, esta secção deve ser lida por cadência e por recorrência. A média mensal de G&A em 2025 foi de USD 3,0M, face a USD 2,85M no orçamento implícito e USD 2,33M em 2024. O mês de maior custo foi Setembro, com USD 3,8M, tendo ficado 12% acima do orçamento do mês e 35% acima do homólogo; esse pico deve ser explicado pela combinação de maior intensidade de serviços técnicos, custos de estrutura mais elevados e reforço de segurança/IT. A mediana mensal de 2025 foi USD 2,9M, a comparar com USD 2,8M no orçamento mensalizado e USD 2,3M em 2024; esta leitura é importante para separar pressão distribuída ao longo do ano de concentrações pontuais.'
              : 'Monthly, this section should be read by cadence and recurrence. The monthly average G&A in 2025 was USD 3.0M, compared to USD 2.85M in implicit budget and USD 2.33M in 2024.'}
          </p>
        </div>

        {/* Driver Sections */}
        <div className="space-y-4">
          {/* 1) Serviços de Suporte */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Serviços de Suporte' : 'Support Services'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? '35% do total G&A' : '35% of total G&A'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">12.5</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Serviços de Suporte fechou em USD 12,5M, +4,2% vs orçamento e +27,6% vs 2024. O aumento não veio de um único item, mas da combinação de Serviços Técnicos de Engenharia (USD 2,0M), Assistência Técnica (USD 1,8M), Avaliação de Diamantes (USD 1,5M), Auditoria/Consultoria (USD 1,5M), Análises Laboratoriais/Estudos (USD 1,2M) e Serviços Jurídicos (USD 1,2M). Em termos económicos, o desvio está associado a maior uso de capacidade técnica externa, suporte especializado à operação e trabalho institucional/compliance.'
                : 'Support Services closed at USD 12.5M, +4.2% budget and +27.6% vs 2024. The increase came from a combination of Technical Engineering Services (USD 2.0M), Technical Assistance (USD 1.8M), Diamond Valuation (USD 1.5M), Audit/Consulting (USD 1.5M), and Legal Services (USD 1.2M).'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'A média mensal do bloco foi de USD 1,04M, face a USD 1,00M no orçamento implícito e USD 0,82M em 2024. Em termos de recorrência, existe aqui um núcleo recorrente estimado em cerca de USD 7,6M/ano, composto por Assistência Técnica, Avaliação de Diamantes, Auditoria/Consultoria, Consultoria Fiscal, Serviços Jurídicos e Honorários e Avenças. O saldo, cerca de USD 4,9M, está mais exposto a engenharia, estudos, contencioso e planeamento, com comportamento menos linear.'
                : 'The monthly average was USD 1.04M, compared to USD 1.00M in implicit budget and USD 0.82M in 2024. There is a recurring core estimated at USD 7.6M/year. The balance, about USD 4.9M, is more exposed to engineering, studies, and planning.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Principais Drivers' : 'Main Drivers'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Serviços Técnicos de Engenharia: USD 2,0M' : 'Technical Engineering Services: USD 2.0M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Assistência Técnica: USD 1,8M' : 'Technical Assistance: USD 1.8M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Núcleo recorrente estimado: USD 7,6M/ano (61%)' : 'Estimated recurring core: USD 7.6M/year (61%)'}
                </li>
              </ul>
            </div>
          </div>

          {/* 2) Custos de Estrutura */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Custos de Estrutura' : 'Structure Costs'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? '37% do total G&A' : '37% of total G&A'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">13.2</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Custos de Estrutura totalizou USD 13,2M, +3,9% vs orçamento e +25,7% vs 2024. O desvio está distribuído por várias linhas de funcionamento: Combustíveis e Lubrificantes (USD 2,3M), Rendas de Imóveis (USD 1,9M), Material de Conservação e Reparação (USD 1,5M), Transportes (USD 1,3M), Electricidade (USD 1,0M) e Aluguer de Equipamento (USD 1,0M). Face ao homólogo, a subida foi generalizada, com maior impacto em transportes, combustíveis, rendas e reparação.'
                : 'Structure Costs totaled USD 13.2M, +3.9% budget and +25.7% vs 2024. The deviation is distributed across several operating lines: Fuels & Lubricants (USD 2.3M), Property Rentals (USD 1.9M), Maintenance Materials (USD 1.5M), Transport (USD 1.3M), Electricity (USD 1.0M) and Equipment Rental (USD 1.0M).'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'A média mensal foi de USD 1,10M, face a USD 1,06M no orçamento e USD 0,88M em 2024. O núcleo recorrente deste bloco é o mais elevado de G&A e totaliza aproximadamente USD 7,9M/ano, incluindo Rendas de Imóveis, Água, Electricidade, Aluguer de Equipamento, Transportes, Limpeza/Higiene/Conforto, Serviços Aduaneiros e Conservação e Reparação corrente. O saldo está mais exposto a combustíveis, materiais e deslocações, com maior sensibilidade a actividade e calendarização.'
                : 'The monthly average was USD 1.10M, compared to USD 1.06M in budget and USD 0.88M in 2024. The recurring core of this block is the highest in G&A and totals approximately USD 7.9M/year.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-amber-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Principais Drivers' : 'Main Drivers'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Combustíveis e Lubrificantes: USD 2,3M' : 'Fuels & Lubricants: USD 2.3M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Rendas de Imóveis: USD 1,9M' : 'Property Rentals: USD 1.9M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Núcleo recorrente estimado: USD 7,9M/ano (60%)' : 'Estimated recurring core: USD 7.9M/year (60%)'}
                </li>
              </ul>
            </div>
          </div>

          {/* 3) Segurança, Seguros e Responsabilidade */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Segurança, Seguros e Responsabilidade' : 'Security, Insurance & Responsibility'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? '15% do total G&A' : '15% of total G&A'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">5.4</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Segurança, Seguros e Responsabilidade encerrou em USD 5,4M, +3,8% vs orçamento e +28,6% vs 2024. O aumento esteve concentrado em Vigilância e Segurança (USD 2,3M) e Seguros (USD 1,5M), complementado por Material de Protecção e Segurança (USD 0,6M), Estudos Ambientais (USD 0,6M) e Quotizações (USD 0,4M). A leitura financeira desta rubrica é de reforço de protecção operacional, cobertura seguradora e exigências ambientais/institucionais.'
                : 'Security, Insurance & Responsibility closed at USD 5.4M, +3.8% budget and +28.6% vs 2024. The increase was concentrated in Surveillance & Security (USD 2.3M) and Insurance (USD 1.5M).'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'A média mensal foi de USD 0,45M, face a USD 0,43M no orçamento e USD 0,35M em 2024. O bloco tem uma componente recorrente elevada. Vigilância, Seguros, Material de Protecção e Quotizações totalizam cerca de USD 4,8M/ano e constituem o núcleo recorrente da categoria. Estudos Ambientais, cerca de USD 0,6M, têm natureza mais variável e dependente de exigências ou campanhas específicas.'
                : 'The monthly average was USD 0.45M, compared to USD 0.43M in budget and USD 0.35M in 2024. The block has a high recurring component totaling about USD 4.8M/year.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Principais Drivers' : 'Main Drivers'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Vigilância e Segurança: USD 2,3M' : 'Surveillance & Security: USD 2.3M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Seguros: USD 1,5M' : 'Insurance: USD 1.5M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Núcleo recorrente estimado: USD 4,8M/ano (89%)' : 'Estimated recurring core: USD 4.8M/year (89%)'}
                </li>
              </ul>
            </div>
          </div>

          {/* 4) IT, Comunicação e Publicidade */}
          <div className="bg-gradient-to-r from-purple-50 to-slate-50 rounded-xl p-5 border border-purple-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Laptop className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'IT, Comunicação e Publicidade' : 'IT, Communication & Advertising'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? '13% do total G&A' : '13% of total G&A'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">4.5</span>
                <span className="text-xs text-slate-500">M USD</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'IT, Comunicação e Publicidade fechou em USD 4,5M, +4,7% vs orçamento e +28,6% vs 2024. O crescimento está concentrado em Licenças Informáticas (USD 1,0M), Serviços Informáticos (USD 0,7M), Suporte ERP e Aplicações (USD 0,7M), Cibersegurança/NIS2 (USD 0,6M) e Comunicações (USD 0,5M). Face ao homólogo, o aumento foi particularmente visível em serviços informáticos, ERP, managed print e cibersegurança, o que indica reforço da base digital e da componente de compliance tecnológica.'
                : 'IT, Communication & Advertising closed at USD 4.5M, +4.7% budget and +28.6% vs 2024. Growth is concentrated in Software Licenses (USD 1.0M), IT Services (USD 0.7M), ERP & Applications Support (USD 0.7M), Cybersecurity/NIS2 (USD 0.6M) and Communications (USD 0.5M).'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'A média mensal foi de USD 0,38M, face a USD 0,36M no orçamento e USD 0,29M em 2024. O núcleo recorrente deste bloco totaliza aproximadamente USD 3,8M/ano, composto por Licenças Informáticas, Serviços Informáticos, Suporte ERP e Aplicações, Managed Print Services, Cibersegurança/NIS2 e Comunicações. Os restantes USD 0,7M correspondem sobretudo a Publicidade e Propaganda e IT – Consultoria e Compliance, com maior probabilidade de natureza não recorrente ou semi-recorrente.'
                : 'The monthly average was USD 0.38M, compared to USD 0.36M in budget and USD 0.29M in 2024. The recurring core totals approximately USD 3.8M/year.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-purple-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Principais Drivers' : 'Main Drivers'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'Licenças Informáticas: USD 1,0M' : 'Software Licenses: USD 1.0M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'Serviços Informáticos + ERP: USD 1,4M' : 'IT Services + ERP: USD 1.4M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'Núcleo recorrente estimado: USD 3,8M/ano (84%)' : 'Estimated recurring core: USD 3.8M/year (84%)'}
                </li>
              </ul>
            </div>
          </div>

          {/* Fecho Executivo */}
          <div className="bg-gradient-to-r from-slate-100 to-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-semibold text-primary text-sm mb-3">{language === 'pt' ? 'Fecho Executivo' : 'Executive Closing'}</h4>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'A evolução de G&A em 2025 foi explicada por uma combinação de aumento da base de funcionamento, maior recurso a serviços técnicos externos, reforço de segurança e seguros e expansão da infra-estrutura digital. Em termos de composição, o bloco mostra uma base recorrente material: somando os quatro grupos, o núcleo recorrente estimado totaliza cerca de USD 24,1M por ano, equivalente a aproximadamente 68% de G&A.'
                : 'G&A evolution in 2025 was explained by a combination of increased operating base, greater use of external technical services, security and insurance reinforcement, and digital infrastructure expansion. The estimated recurring core totals about USD 24.1M per year, equivalent to approximately 68% of G&A.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed">
              {language === 'pt' 
                ? 'O saldo, cerca de USD 11,5M, está mais exposto a projectos, estudos, serviços especializados, engenharia, publicidade e outros custos de natureza menos linear. Para leitura de gerência, a questão central nas páginas seguintes é separar o que passou a fazer parte da nova base recorrente de custo do que deve ser tratado como despesa pontual ou dependente de âmbito.'
                : 'The balance, about USD 11.5M, is more exposed to projects, studies, specialized services, engineering, advertising and other costs of less linear nature. For management reading, the central question is separating what became part of the new recurring cost base from what should be treated as one-off expense.'}
            </p>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* Category Distribution - Pie Chart and Cards */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Composição Gerais e Administrativos por Categoria' : 'General & Admin Composition by Category'}
        </h3>
        <p className="text-sm text-slate-500 mb-6 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100">
            <ChevronDown className="w-3 h-3 text-blue-600" />
          </span>
          {language === 'pt' 
            ? 'Clique nos blocos abaixo para análise mais pormenorizada de cada categoria.' 
            : 'Click on the blocks below for detailed analysis of each category.'}
          {expandedCategory && (
            <button 
              onClick={() => setExpandedCategory(null)}
              className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
            >
              {language === 'pt' ? 'Limpar seleção' : 'Clear selection'}
            </button>
          )}
        </p>
        <div 
          className="flex items-start gap-8"
          onClick={(e) => {
            // If clicking on the container background (not on a card), clear selection
            if (e.target === e.currentTarget) {
              setExpandedCategory(null);
            }
          }}
        >
          <ResponsiveContainer width="45%" height={320}>
            <PieChart onClick={() => setExpandedCategory(null)} style={{ cursor: expandedCategory ? 'pointer' : 'default' }}>
              <Pie
                data={categoryPieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
                label={({ cx, cy, midAngle, outerRadius, percent, index }) => {
                  const RADIAN = Math.PI / 180;
                  const radius = outerRadius + 28;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  const cat = categories[index];
                  const isSelected = expandedCategory === cat?.id;
                  const hasSelection = expandedCategory !== null;
                  
                  return (
                    <text 
                      x={x} 
                      y={y} 
                      fill={hasSelection && isSelected ? cat?.color : '#374151'}
                      textAnchor={x > cx ? 'start' : 'end'} 
                      dominantBaseline="central"
                      style={{
                        fontSize: hasSelection && isSelected ? '15px' : '12px',
                        fontWeight: hasSelection && isSelected ? 700 : 600
                      }}
                    >
                      {`${(percent * 100).toFixed(0)}%`}
                    </text>
                  );
                }}
                labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                isAnimationActive={false}
              >
                {categoryPieData.map((entry, index) => {
                  const cat = categories[index];
                  const isSelected = expandedCategory === cat?.id;
                  const hasSelection = expandedCategory !== null;
                  
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.fill}
                      fillOpacity={hasSelection ? (isSelected ? 1 : 0.4) : 1}
                      stroke={isSelected ? '#ffffff' : 'none'}
                      strokeWidth={isSelected ? 4 : 0}
                    />
                  );
                })}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div 
            className="flex-1 grid grid-cols-2 gap-4"
            onClick={(e) => {
              // If clicking on the grid background (gap area), clear selection
              if (e.target === e.currentTarget) {
                setExpandedCategory(null);
              }
            }}
          >
            {categories.map((cat, idx) => {
              const catData = gaData[cat.key];
              const catVsBudget = catData?.vsBudget || 0;
              const isExpanded = expandedCategory === cat.id;
              const hasExpanded = expandedCategory !== null;
              
              return (
                <div 
                  key={idx} 
                  className={`rounded-xl cursor-pointer transition-all duration-500 ease-out border-2 
                    ${isExpanded 
                      ? 'bg-white shadow-2xl border-slate-400 ring-4 ring-offset-4 z-20 p-5' 
                      : hasExpanded 
                        ? 'bg-slate-100 opacity-30 hover:opacity-50 border-transparent p-4 scale-95' 
                        : 'bg-slate-50 hover:shadow-md border-transparent hover:border-slate-200 p-4'
                    }`}
                  onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                  style={{ 
                    borderLeftColor: cat.color, 
                    borderLeftWidth: isExpanded ? '6px' : '4px',
                    ringColor: isExpanded ? cat.color : 'transparent',
                    transform: isExpanded ? 'scale(1.15)' : hasExpanded ? 'scale(0.95)' : 'scale(1)',
                    transformOrigin: 'center center'
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <cat.icon className={`${isExpanded ? 'w-5 h-5' : 'w-4 h-4'}`} style={{ color: cat.color }} />
                      <span className={`font-semibold text-primary ${isExpanded ? 'text-base' : 'text-sm'}`}>{cat.name[language]}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-primary" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className={`font-serif font-bold text-primary ${isExpanded ? 'text-3xl' : 'text-xl'}`}>
                      {(catData?.total || 0).toFixed(1)}M
                    </span>
                    <span className={`text-slate-500 ${isExpanded ? 'text-sm' : 'text-xs'}`}>
                      ({gaData.total ? ((catData?.total / gaData.total) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className={`flex flex-wrap items-center gap-2 mt-2 ${isExpanded ? 'text-sm' : 'text-xs'}`}>
                    <span className={`px-1.5 py-0.5 rounded ${catVsBudget <= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                      {catVsBudget > 0 ? '+' : ''}{catVsBudget.toFixed(1)}% {language === 'pt' ? 'vs Orç.' : 'vs Bdgt'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded ${catData?.yoyChange <= 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                      {catData?.yoyChange > 0 ? '+' : ''}{catData?.yoyChange?.toFixed(1)}% YoY
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Expanded Category Detail */}
        {expandedCategory && (
          <div className="mt-6">
            {categories.filter(c => c.id === expandedCategory).map(cat => (
              <div key={cat.id}>
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-200">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: cat.lightColor }}>
                    <cat.icon className="w-5 h-5" style={{ color: cat.color }} />
                  </div>
                  <div>
                    <h4 className="font-serif text-lg font-semibold text-primary">{cat.name[language]}</h4>
                    <p className="text-xs text-slate-500">{cat.description[language]}</p>
                  </div>
                </div>
                {renderCategoryDetail(cat)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralAdminAnalysis;
